import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Paper,
  Chip,
  Rating,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useCart } from '../context/CartContext';
import { Helmet } from 'react-helmet';

// Import images
import heroBg from '../assets/images/hero-bg.jpg';
import feature1 from '../assets/images/feature1.jpg';
import feature2 from '../assets/images/feature2.jpg';
import feature3 from '../assets/images/feature3.jpg';

// Import product images
import qrBasic from '../assets/images/qr-basic.jpg';
import qrFamily from '../assets/images/qr-family.jpg';
// Import other product images as needed

// Create a map for product images
const productImages = {
  'qr-basic.jpg': qrBasic,
  'qr-family.jpg': qrFamily,
  // Add other mappings as needed
};

// Function to resolve image paths
const resolveImagePath = (imagePath) => {
  if (!imagePath) return '';
  
  // If it's already a full URL
  if (imagePath.startsWith('http')) return imagePath;
  
  // Extract the filename from the path
  const filename = imagePath.split('/').pop();
  
  // If we have this image in our map, return the imported version
  if (productImages[filename]) {
    return productImages[filename];
  }
  
  // Fallback to the API URL path for server-side images
  return `${process.env.REACT_APP_API_URL || ''}${imagePath}`;
};

const HeroSection = styled(Box)`
  background: linear-gradient(135deg, rgba(33, 33, 33, 0.4), rgba(0, 0, 0, 0.4)),
    url(${heroBg}) center/cover;
  color: white;
  padding: 140px 0 120px;
  text-align: center;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 70% 30%, rgba(255, 255, 255, 0.1), transparent 70%);
  }
`;

const FeatureCard = styled(Card)`
  height: 100%;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);
  
  &:hover {
    transform: translateY(-12px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }
`;

const StyledButton = styled(Button)`
  border-radius: 30px;
  padding: 12px 32px;
  font-weight: 600;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  text-transform: none;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }
`;

const SectionTitle = styled(Typography)`
  position: relative;
  padding-bottom: 16px;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: linear-gradient(90deg, #757575, #9e9e9e);
    border-radius: 4px;
  }
`;

const ProductCard = styled(Paper)`
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
    transform: translateY(-5px);
  }
`;

const PriceTag = styled(Typography)`
  font-weight: 700;
  color: #616161;
  font-size: 1.75rem;
  
  .currency {
    font-size: 1rem;
    font-weight: 500;
    vertical-align: super;
    margin-right: 2px;
  }
  
  .cents {
    font-size: 1rem;
    vertical-align: super;
  }
`;

const TestimonialCard = styled(Paper)`
  padding: 24px;
  border-radius: 16px;
  height: 100%;
  position: relative;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.07);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }
  
  &:before {
    content: '"';
    position: absolute;
    top: 10px;
    left: 20px;
    font-size: 120px;
    color: rgba(0, 0, 0, 0.03);
    font-family: Georgia, serif;
    line-height: 1;
  }
`;

const Home = () => {
  const navigate = useNavigate();
  const { cart } = useCart();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // New state for products
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productError, setProductError] = useState('');
  
  // Contact form modal state
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactSubmitting, setContactSubmitting] = useState(false);

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/products`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch products');
        }

        const data = await response.json();
        setProducts(data || []);
        setProductError('');
      } catch (error) {
        console.error('Error fetching products:', error);
        setProductError('Failed to load products. Please try again later.');
        // Fallback to hardcoded products if API fails
        setProducts([
          {
            id: 'basic',
            name: 'Basic Memorial Package',
            description: 'Single stainless steel QR code, perfect for mounting on a gravestone.',
            price: 75.00,
            features: ['1 Stainless Steel QR Code', 'Perfect for Gravestones', 'Up to 20 Photos', 'Weather & UV Resistant', 'Easy Installation'],
            imageUrl: '/uploads/products/qr-basic.jpg',
            popular: false,
          },
          {
            id: 'family',
            name: 'Family Memorial Package',
            description: 'Complete memorial solution with gravestone QR code and heart-shaped keyrings for family members.',
            price: 150.00,
            features: ['1 Stainless Steel QR Code for Gravestone', '3 Heart-Shaped QR Keyrings for Family', 'Shared Memorial Access', 'Up to 50 Photos', 'Premium Support'],
            imageUrl: '/uploads/products/qr-family.jpg',
            popular: true,
          },
        ]);
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();
  }, []);

  // Debug image paths - commented out for production to improve performance
  /*
  useEffect(() => {
    if (products.length > 0) {
      console.log('Product image debug info (Home.js):');
      console.log('Original imageUrl:', products[0].imageUrl);
      console.log('Resolved URL:', resolveImagePath(products[0].imageUrl));
    }
  }, [products]);
  */

  const features = [
    {
      title: 'Create Digital Memorials',
      description: 'Honor your loved ones with beautiful, lasting digital memorials accessible from anywhere.',
      image: feature1,
    },
    {
      title: 'Share Memories',
      description: 'Upload photos, videos, and stories to preserve precious moments for generations to come.',
      image: feature2,
    },
    {
      title: 'QR Code Access',
      description: 'Provide simple, instant access to memorials through unique QR codes that can be placed anywhere.',
      image: feature3,
    },
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      text: 'Creating a memorial for my father was a healing experience. The QR code on his headstone allows visitors to learn about his life and see photos that show what a wonderful person he was.',
      rating: 5,
    },
    {
      name: 'Michael Torres',
      text: 'The family package has been perfect for preserving our family history. I appreciate how easy it is to update and add new memories over time.',
      rating: 5,
    },
    {
      name: 'Emily Parker',
      text: 'The metal QR code has held up perfectly outdoors. I love that anyone who visits can scan it and immediately connect with my mother\'s life story.',
      rating: 4,
    },
  ];

  // Use the cart context instead of local state
  const { addToCart, updateQuantity, removeFromCart, getTotalItems } = useCart();
  
  const handleAddToCart = (productId) => {
    addToCart(productId, 1);
    
    setSnackbar({
      open: true,
      message: 'Item added to cart',
      severity: 'success',
    });
  };

  const handleRemoveFromCart = (productId) => {
    // Get current quantity first
    const currentQuantity = cart[productId] || 0;
    
    if (currentQuantity > 1) {
      updateQuantity(productId, currentQuantity - 1);
    } else {
      removeFromCart(productId);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };
  
  // Contact form handlers
  const handleOpenContactModal = () => {
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  const handleContactFormChange = (e) => {
    setContactForm({ ...contactForm, [e.target.name]: e.target.value });
  };

  const handleContactSubmit = async () => {
    try {
      setContactSubmitting(true);
      
      // Try to fetch to your server API endpoint if it exists
      try {
        await fetch(`${process.env.REACT_APP_API_URL || ''}/api/contact`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...contactForm,
            recipient: 'jruizp39@hotmail.com' // Send to this email
          }),
        });
      } catch (error) {
        console.error('Server contact endpoint error:', error);
        // Fallback to client-side email handling
        // This is a fallback in case your API isn't ready yet
        // In production, you should rely on your server API
        window.open(`mailto:jruizp39@hotmail.com?subject=Digital Memorial Inquiry from ${contactForm.name}&body=${encodeURIComponent(`Name: ${contactForm.name}\nEmail: ${contactForm.email}\n\nMessage:\n${contactForm.message}`)}`);
      }
      
      setSnackbar({
        open: true,
        message: 'Message sent successfully. We\'ll be in touch soon!',
        severity: 'success',
      });
      
      // Reset form and close modal
      setContactForm({ name: '', email: '', message: '' });
      handleCloseContactModal();
    } catch (error) {
      console.error('Error sending message:', error);
      setSnackbar({
        open: true,
        message: 'Failed to send message. Please try again later.',
        severity: 'error',
      });
    } finally {
      setContactSubmitting(false);
    }
  };

  // Create product schema for structured data
  const generateProductSchemaItems = () => {
    return products.map((product, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": product.name,
        "description": product.description,
        "offers": {
          "@type": "Offer",
          "price": product.price.toFixed(2),
          "priceCurrency": "GBP",
          "availability": "https://schema.org/InStock"
        }
      }
    }));
  };

  return (
    <>
      
<Helmet>
  {/* Preload critical hero image for faster rendering */}
  <link rel="preload" href={heroBg} as="image" />

  <title>Digital Memorials UK | QR Codes for Gravestones | Memorial QR Codes</title>
  <meta name="description" content="Premium Digital Memorial QR codes for gravestones, headstones and memorials. Create lasting digital tributes that connect physical monuments with unlimited digital memories." />
  <meta name="keywords" content="keywords, QR code for gravestone, digital memorial QR code, headstone QR code, memorial QR code, gravestone QR code UK, QR code for headstone, QR codes for cemetery, digital gravestones, digital memorial service, grave marker QR code, digital memorials UK, memorial QR codes, QR code memorial plaque, QR code granite memorial, QR code for ashes, QR code remembrance" />
  <link rel="canonical" href="https://digitalmemorials.co.uk" />
  
  {/* Open Graph / Facebook */}
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://digitalmemorials.co.uk/" />
  <meta property="og:title" content="Digital Memorials UK | QR Codes for Gravestones" />
  <meta property="og:description" content="Create lasting digital memorials with premium QR codes for gravestones and memorials. Honor, Remember, Connect." />
  <meta property="og:image" content="https://digitalmemorials.co.uk/logo-og.jpg" />

  {/* Twitter */}
  <meta property="twitter:card" content="summary_large_image" />
  <meta property="twitter:url" content="https://digitalmemorials.co.uk/" />
  <meta property="twitter:title" content="Digital Memorials UK | QR Codes for Gravestones" />
  <meta property="twitter:description" content="Create lasting digital memorials with premium QR codes for gravestones and memorials." />
  <meta property="twitter:image" content="https://digitalmemorials.co.uk/logo-og.jpg" />
  
  {/* Structured data for Organization */}
  <script type="application/ld+json">
    {`
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Digital Memorials UK",
        "url": "https://digitalmemorials.co.uk/",
        "logo": "https://digitalmemorials.co.uk/logo.jpg",
        "sameAs": [
          "https://facebook.com/digitalmemorialsuk",
          "https://instagram.com/digitalmemorialsuk"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+44-XXX-XXXX-XXX",
          "contactType": "customer service",
          "areaServed": "UK"
        }
      }
    `}
  </script>
  
  {/* Structured data for WebSite */}
  <script type="application/ld+json">
    {`
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "url": "https://digitalmemorials.co.uk/",
        "name": "Digital Memorials UK",
        "description": "Premium QR codes for gravestones and memorials. Create lasting digital tributes to honor loved ones.",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://digitalmemorials.co.uk/search?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      }
    `}
  </script>
  
  {/* Structured data for Products */}
  <script type="application/ld+json">
    {`
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "itemListElement": ${JSON.stringify(generateProductSchemaItems())}
      }
    `}
  </script>
  
  {/* Structured data for LocalBusiness */}
  <script type="application/ld+json">
    {`
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Digital Memorials UK",
        "image": "https://digitalmemorials.co.uk/logo.jpg",
        "priceRange": "£££",
        "description": "We provide premium QR codes for gravestones and memorials, allowing visitors to access digital memories, photos, and tributes by scanning with their smartphone.",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Your Street Address",
          "addressLocality": "Your City",
          "postalCode": "Your Postal Code",
          "addressCountry": "UK"
        },
        "telephone": "+44-XXX-XXXX-XXX",
        "url": "https://digitalmemorials.co.uk",
        "openingHoursSpecification": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "09:00",
          "closes": "17:00"
        }
      }
    `}
  </script>
  
  {/* Structured data for FAQ */}
  <script type="application/ld+json">
    {`
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What is a Digital Memorial QR Code?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "A Digital Memorial QR Code is a durable QR code that can be attached to gravestones, plaques, or memorials. When scanned with a smartphone, it takes visitors to a dedicated digital memorial page containing photos, biography, and memories of your loved one."
            }
          },
          {
            "@type": "Question",
            "name": "How long do the QR codes last?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Our QR codes are made from premium stainless steel and are designed to last for generations. They are weather-resistant, UV-resistant, and built to withstand outdoor conditions for decades."
            }
          },
          {
            "@type": "Question",
            "name": "How do I install the QR code on a gravestone?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Each QR code comes with strong adhesive backing suitable for stone surfaces. Simply clean the surface, peel the backing, and press firmly. For added security, we also provide optional mounting hardware for permanent installation."
            }
          },
          {
            "@type": "Question",
            "name": "Can family members add photos and memories?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, you can authorize family members to add photos, stories, and memories to the digital memorial. You control who has access to add content while the memorial remains publicly accessible for visitors."
            }
          },
          {
            "@type": "Question",
            "name": "Is there an ongoing subscription fee?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "No. Once you purchase a Digital Memorial QR Code, the digital memorial is hosted for a lifetime without additional charges or subscription fees."
            }
          }
        ]
      }
    `}
  </script>
</Helmet>
      
      <Box>
        {/* Hero Section - using semantic header */}
        <header>
          <HeroSection>
            <Container maxWidth="md">
              <Typography 
                variant="h1" 
                component="h1" 
                gutterBottom 
                sx={{ 
                  fontWeight: 800,
                  letterSpacing: '0.5px',
                  textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                  fontSize: { xs: '2.5rem', sm: '3.5rem' }
                }}
              >
                Honor. Remember. Connect.
              </Typography>
              <Typography 
                variant="h5" 
                paragraph
                sx={{ 
                  maxWidth: '700px',
                  margin: '0 auto',
                  opacity: 0.9,
                  lineHeight: 1.6,
                  mb: 4,
                  fontSize: { xs: '1.1rem', sm: '1.25rem' }
                }}
              >
                Create lasting digital memorials that connect physical spaces with unlimited digital memories
              </Typography>
              <Box sx={{ mt: 5, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2 }}>
                <StyledButton
                  variant="contained"
                  size="large"
                  color="secondary"
                  onClick={() => navigate('/create-memorial')}
                >
                  Create Memorial
                </StyledButton>
                <StyledButton
                  variant="outlined"
                  size="large"
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.15)', 
                    color: 'white',
                    borderColor: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.25)',
                      borderColor: 'white',
                    }
                  }}
                  onClick={() => {
                    const shopSection = document.getElementById('shop-section');
                    shopSection.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Shop QR Codes
                </StyledButton>
              </Box>
            </Container>
          </HeroSection>
        </header>

        {/* Features Section */}
        <section aria-labelledby="features-heading">
          <Container maxWidth="lg" sx={{ py: 10 }}>
            <Box sx={{ mb: 7, textAlign: 'center' }}>
              <SectionTitle
                variant="h2"
                component="h2"
                id="features-heading"
                textAlign="center"
                gutterBottom
                color="grey.800"
                sx={{ mb: 2 }}
              >
                Our Features
              </SectionTitle>
              <Typography 
                variant="subtitle1" 
                color="text.secondary"
                sx={{ maxWidth: '700px', mx: 'auto' }}
              >
                Preserving memories with modern technology for generations to come
              </Typography>
            </Box>

            <Grid container spacing={5}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <FeatureCard elevation={0}>
                    <CardMedia
                      component="img"
                      height="220"
                      width="400"
                      loading={index > 0 ? "lazy" : "eager"}
                      image={feature.image}
                      alt={feature.title}
                      sx={{ 
                        transition: 'transform 0.5s ease',
                        '&:hover': { transform: 'scale(1.05)' } 
                      }}
                    />
                    <CardContent sx={{ p: 3 }}>
                      <Typography 
                        variant="h3" 
                        component="h3" 
                        gutterBottom 
                        sx={{ 
                          fontWeight: 700,
                          mb: 2,
                          color: 'grey.800',
                          fontSize: '1.5rem'
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography 
                        color="text.secondary"
                        sx={{ lineHeight: 1.7 }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </FeatureCard>
                </Grid>
              ))}
            </Grid>
          </Container>
        </section>
        
        {/* Shop Section */}
        <section aria-labelledby="shop-heading" id="shop-section">
          <Box 
            sx={{ 
              py: 10, 
              background: 'linear-gradient(180deg, #f9f9f9 0%, #ffffff 100%)' 
            }}
          >
            <Container maxWidth="lg">
              <Box sx={{ mb: 7, textAlign: 'center' }}>
                <SectionTitle
                  variant="h2"
                  component="h2"
                  id="shop-heading"
                  textAlign="center"
                  gutterBottom
                  color="grey.800"
                  sx={{ mb: 2 }}
                >
                  Memorial QR Codes
                </SectionTitle>
                <Typography 
                  variant="subtitle1" 
                  color="text.secondary"
                  sx={{ maxWidth: '700px', mx: 'auto' }}
                >
                  Our premium QR codes connect physical locations to digital memories, lasting for generations
                </Typography>
              </Box>

              {loadingProducts ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                </Box>
              ) : productError ? (
                <Alert severity="error" sx={{ mb: 3 }}>{productError}</Alert>
              ) : (
                <Grid container spacing={4} sx={{ mb: 5 }}>
                  {products.map((product, index) => (
                    <Grid item xs={12} md={6} key={product.id}>
                      <ProductCard elevation={2} sx={{ position: 'relative' }}>
                        {product.popular && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 15,
                              right: 15,
                              zIndex: 1,
                            }}
                          >
                            <Chip
                              label="Most Popular"
                              color="secondary"
                              size="small"
                              sx={{ fontWeight: 'bold' }}
                            />
                          </Box>
                        )}
                        <CardMedia
                          component="img"
                          height="200"
                          width="400"
                          loading={index > 0 ? "lazy" : "eager"}
                          image={resolveImagePath(product.imageUrl)}
                          alt={product.name}
                          sx={{ objectFit: 'cover' }}
                        />
                        <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="h3" component="h3" fontWeight="bold" gutterBottom sx={{ color: 'grey.800', fontSize: '1.5rem' }}>
                            {product.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 2 }}>
                            {product.description}
                          </Typography>
                          
                          <PriceTag sx={{ my: 2 }}>
                            <span className="currency">£</span>
                            {Math.floor(product.price)}
                            <span className="cents">{(product.price % 1).toFixed(2).substring(2)}</span>
                          </PriceTag>
                          
                          <Box sx={{ mt: 1, mb: 3 }}>
                            {product.features.map((feature, idx) => (
                              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <CheckCircleIcon sx={{ mr: 1, color: 'grey.600' }} fontSize="small" />
                                <Typography variant="body2" color="grey.700">{feature}</Typography>
                              </Box>
                            ))}
                          </Box>
                          
                          <Box sx={{ mt: 'auto' }}>
                            {cart[product.id] ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                  <IconButton size="small" onClick={() => handleRemoveFromCart(product.id)}>
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                  <Typography sx={{ px: 2 }}>{cart[product.id]}</Typography>
                                  <IconButton size="small" onClick={() => handleAddToCart(product.id)}>
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                                <Button 
                                  variant="contained" 
                                  color="primary"
                                  onClick={handleCheckout}
                                  sx={{ ml: 1 }}
                                >
                                  Checkout
                                </Button>
                              </Box>
                            ) : (
                              <Button
                                fullWidth
                                variant="contained"
                                color="primary"
                                size="large"
                                startIcon={<ShoppingCartIcon />}
                                onClick={() => handleAddToCart(product.id)}
                                sx={{ borderRadius: 2 }}
                              >
                                Add to Cart
                              </Button>
                            )}
                          </Box>
                        </CardContent>
                      </ProductCard>
                    </Grid>
                  ))}
                </Grid>
              )}
              
              <Box sx={{ textAlign: 'center', mt: 4, p: 3, bgcolor: 'rgba(0, 0, 0, 0.03)', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom fontWeight="medium" sx={{ color: 'grey.800' }}>
                  Need a custom solution?
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: 'grey.700' }}>
                  Contact us for bulk orders, custom designs, or special requirements
                </Typography>
                <Button 
                  variant="outlined" 
                  color="primary"
                  onClick={handleOpenContactModal}
                  sx={{ textTransform: 'none', borderRadius: 30, px: 3 }}
                >
                  Get in Touch
                </Button>
              </Box>
            </Container>
          </Box>
        </section>

        {/* Testimonials Section */}
        <section aria-labelledby="testimonials-heading">
          <Container maxWidth="lg" sx={{ py: 10 }}>
            <Box sx={{ mb: 7, textAlign: 'center' }}>
              <SectionTitle
                variant="h2"
                component="h2"
                id="testimonials-heading"
                textAlign="center"
                gutterBottom
                color="grey.800"
                sx={{ mb: 2 }}
              >
                What Our Customers Say
              </SectionTitle>
              <Typography 
                variant="subtitle1" 
                color="text.secondary"
                sx={{ maxWidth: '700px', mx: 'auto' }}
              >
                Real stories from families preserving their precious memories
              </Typography>
            </Box>
            
            <Grid container spacing={4}>
              {testimonials.map((testimonial, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <TestimonialCard>
                    <Rating value={testimonial.rating} readOnly sx={{ mb: 2, color: 'grey.600' }} />
                    <Typography 
                      variant="body1" 
                      paragraph 
                      sx={{ 
                        fontStyle: 'italic',
                        position: 'relative',
                        zIndex: 1,
                        minHeight: '100px',
                        color: 'grey.700'
                      }}
                    >
                      "{testimonial.text}"
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'grey.800' }}>
                      {testimonial.name}
                    </Typography>
                  </TestimonialCard>
                </Grid>
              ))}
            </Grid>
          </Container>
        </section>
        {/* FAQ Section - Add this before the Call to Action section in Home.js */}
<section aria-labelledby="faq-heading">
  <Box 
    sx={{ 
      py: 10, 
      background: 'linear-gradient(180deg, #ffffff 0%, #f5f5f5 100%)' 
    }}
  >
    <Container maxWidth="lg">
      <Box sx={{ mb: 7, textAlign: 'center' }}>
        <SectionTitle
          variant="h2"
          component="h2"
          id="faq-heading"
          textAlign="center"
          gutterBottom
          color="grey.800"
          sx={{ mb: 2 }}
        >
          Common Questions
        </SectionTitle>
        <Typography 
          variant="subtitle1" 
          color="text.secondary"
          sx={{ maxWidth: '700px', mx: 'auto' }}
        >
          Everything you need to know about our memorial QR codes
        </Typography>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              height: '100%', 
              border: '1px solid', 
              borderColor: 'grey.200',
              borderRadius: 2
            }}
          >
            <Typography variant="h6" gutterBottom fontWeight="bold" color="grey.800">
              What is a QR code for gravestones?
            </Typography>
            <Typography variant="body1" color="text.secondary">
              A QR code for gravestones is a durable weatherproof code that can be attached to headstones, memorials, and plaques. When scanned with a smartphone, it takes visitors to a dedicated digital memorial page containing photos, videos, biography, and memories of your loved one.
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              height: '100%', 
              border: '1px solid', 
              borderColor: 'grey.200',
              borderRadius: 2
            }}
          >
            <Typography variant="h6" gutterBottom fontWeight="bold" color="grey.800">
              How long do your memorial QR codes last?
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Our QR codes are made from premium stainless steel and are designed to last for generations. They are weather-resistant, UV-resistant, and built to withstand outdoor conditions for decades, making them perfect for gravestones and outdoor memorials.
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              height: '100%', 
              border: '1px solid', 
              borderColor: 'grey.200',
              borderRadius: 2
            }}
          >
            <Typography variant="h6" gutterBottom fontWeight="bold" color="grey.800">
              How do I install a QR code on a gravestone?
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Each QR code comes with strong adhesive backing suitable for stone surfaces. Simply clean the surface, peel the backing, and press firmly. For added security, we also provide optional mounting hardware for permanent installation on headstones and memorials.
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              height: '100%', 
              border: '1px solid', 
              borderColor: 'grey.200',
              borderRadius: 2
            }}
          >
            <Typography variant="h6" gutterBottom fontWeight="bold" color="grey.800">
              Can I use memorial QR codes for cremation urns and plaques?
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Yes, our QR codes are designed to be versatile and can be attached to cremation urns, plaques, benches, and other memorial items. We offer different sizes to accommodate various memorial types.
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              height: '100%', 
              border: '1px solid', 
              borderColor: 'grey.200',
              borderRadius: 2
            }}
          >
            <Typography variant="h6" gutterBottom fontWeight="bold" color="grey.800">
              Is there an ongoing subscription fee for digital memorials?
            </Typography>
            <Typography variant="body1" color="text.secondary">
              No. Once you purchase a Digital Memorial QR Code, the digital memorial is hosted for a lifetime without additional charges or subscription fees. This ensures your loved one's memory is preserved permanently.
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              height: '100%', 
              border: '1px solid', 
              borderColor: 'grey.200',
              borderRadius: 2
            }}
          >
            <Typography variant="h6" gutterBottom fontWeight="bold" color="grey.800">
              Do you offer QR codes for cemetery headstones throughout the UK?
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Yes, we provide our memorial QR code services throughout the entire UK. Our products can be shipped anywhere in the United Kingdom, and we offer installation advice for all types of cemetery headstones and memorials.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  </Box>
</section>
        {/* Call to Action */}
        <section aria-labelledby="cta-heading">
          <Box 
            sx={{ 
              py: 10, 
              bgcolor: 'grey.800',
              color: 'white',
              textAlign: 'center'
            }}
          >
            <Container maxWidth="md">
              <Typography 
                variant="h2" 
                component="h2" 
                id="cta-heading"
                gutterBottom
                sx={{ 
                  fontWeight: 700,
                  mb: 3
                }}
              >
                Start Preserving Memories Today
              </Typography>
              <Typography 
                variant="h6"
                sx={{ 
                  maxWidth: '700px',
                  mx: 'auto',
                  mb: 5,
                  opacity: 0.9
                }}
              >
                Create a lasting memorial that connects physical monuments with limitless digital memories
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                <StyledButton
                  variant="contained"
                  size="large"
                  color="secondary"
                  onClick={() => navigate('/create-memorial')}
                >
                  Create Memorial
                </StyledButton>
                <StyledButton
                  variant="outlined"
                  size="large"
                  sx={{ 
                    color: 'white',
                    borderColor: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)'
                    }
                  }}
                  onClick={() => {
                    const shopSection = document.getElementById('shop-section');
                    shopSection.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Shop QR Codes
                </StyledButton>
              </Box>
            </Container>
          </Box>
        </section>
        
        {/* Cart notification */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
        
        {/* Floating cart button - only shows when items are in cart */}
        {getTotalItems() > 0 && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1000,
            }}
          >
            <Tooltip title="View cart">
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleCheckout}
                    startIcon={<ShoppingCartIcon />}
                    sx={{
                      borderRadius: 30,
                      py: 1.5,
                      px: 3,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    }}
                  >
                    {getTotalItems()} {getTotalItems() === 1 ? 'item' : 'items'} · Checkout
                  </Button>
                </Tooltip>
              </Box>
            )}
          </Box>

        {/* Contact Form Modal */}
        <Dialog open={contactModalOpen} onClose={handleCloseContactModal} maxWidth="sm" fullWidth>
          <DialogTitle>Contact Us</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Have questions about our memorial QR codes or need a custom solution? Fill out the form below and we'll get back to you as soon as possible.
            </Typography>
            <TextField
              fullWidth
              margin="normal"
              label="Your Name"
              name="name"
              value={contactForm.name}
              onChange={handleContactFormChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Email Address"
              name="email"
              type="email"
              value={contactForm.email}
              onChange={handleContactFormChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Message"
              name="message"
              multiline
              rows={4}
              value={contactForm.message}
              onChange={handleContactFormChange}
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseContactModal}>Cancel</Button>
            <Button 
              onClick={handleContactSubmit} 
              variant="contained" 
              color="primary"
              disabled={contactSubmitting || !contactForm.name || !contactForm.email || !contactForm.message}
            >
              {contactSubmitting ? <CircularProgress size={24} /> : 'Send Message'}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  };

  export default Home;